import React, { useRef } from 'react';
import { FilmIcon } from '../icons/FilmIcon';
import { PhotoIcon } from '../icons/PhotoIcon';
import { MusicIcon } from '../icons/MusicIcon';
import { SoundIcon } from '../icons/SoundIcon';
import { TextIcon } from '../icons/TextIcon';
import { v4 as uuidv4 } from 'uuid';
import { ChevronLeftIcon } from '../icons/ChevronLeftIcon';
import { ChevronRightIcon } from '../icons/ChevronRightIcon';

const readFileAsBase64 = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve({ name: file.name, data: reader.result });
        reader.onerror = error => reject(error);
        reader.readAsDataURL(file);
    });
};

export const AssetPanel = ({ project, onAddTextClip, onAddAudioClip, onAddAsset, isCollapsed, setIsCollapsed }) => {
    const musicInputRef = useRef(null);
    const sfxInputRef = useRef(null);

    const handleFileUpload = async (event, trackName) => {
        const file = event.target.files?.[0];
        if (!file) return;

        try {
            const { name, data } = await readFileAsBase64(file);
            const newAsset = {
                id: `asset-audio-${uuidv4()}`,
                type: 'audio',
                name,
                data,
            };
            onAddAsset(newAsset);
            onAddAudioClip(newAsset, trackName);
        } catch (error) {
            console.error("Error reading file:", error);
        }
        
        if (event.target) {
            event.target.value = '';
        }
    };
    
    const shots = project.scenes.flatMap(s => s.shots);
    const musicAssets = project.assets.filter(a => a.type === 'audio');

    if (isCollapsed) {
        return (
            <div className="bg-[#1f1f29] border-r border-gray-700 flex flex-col p-2 items-center shrink-0">
                <button 
                    onClick={() => setIsCollapsed(false)} 
                    className="p-2 rounded-md hover:bg-gray-700 transition-colors"
                    aria-label="Expand Asset Panel"
                >
                    <ChevronRightIcon className="w-5 h-5" />
                </button>
            </div>
        );
    }

    return (
        <div className="w-[300px] bg-[#1f1f29] border-r border-gray-700 flex flex-col p-4 space-y-4 overflow-y-auto shrink-0 relative">
             <button 
                onClick={() => setIsCollapsed(true)} 
                className="absolute top-2 right-2 p-2 rounded-md hover:bg-gray-700 transition-colors z-10"
                aria-label="Collapse Asset Panel"
             >
                <ChevronLeftIcon className="w-5 h-5" />
             </button>
             <h2 className="text-xl font-bold">Assets</h2>

            {/* Visuals */}
            <div>
                <h3 className="text-sm font-semibold text-gray-400 mb-2 uppercase">Visuals</h3>
                <div className="space-y-2">
                     <button onClick={onAddTextClip} className="w-full flex items-center p-2 rounded-md bg-gray-700/50 hover:bg-gray-700 transition-colors">
                        <TextIcon className="w-5 h-5 mr-3 text-purple-400" />
                        <span className="text-sm">Add Text Overlay</span>
                    </button>
                    {shots.map((shot, index) => (
                        <div key={shot.id} className="flex items-center p-2 rounded-md bg-gray-800/60">
                            <div className="w-12 h-8 bg-gray-700 rounded-sm mr-3 flex-shrink-0 flex items-center justify-center">
                                {shot.startFrameImage ? <img src={shot.startFrameImage} className="w-full h-full object-cover" crossOrigin="anonymous" referrerPolicy="no-referrer"/> : <FilmIcon className="w-5 h-5 text-gray-500" />}
                            </div>
                            <p className="text-sm truncate">Shot {index + 1}: {shot.description}</p>
                        </div>
                    ))}
                    {project.characters.map(char => (
                         <div key={char.id} className="flex items-center p-2 rounded-md bg-gray-800/60">
                            <div className="w-12 h-8 bg-gray-700 rounded-sm mr-3 flex-shrink-0 flex items-center justify-center">
                                 {char.image ? <img src={char.image} className="w-full h-full object-cover" crossOrigin="anonymous" referrerPolicy="no-referrer"/> : <PhotoIcon className="w-5 h-5 text-gray-500" />}
                            </div>
                            <p className="text-sm font-medium">{char.name}</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* Audio */}
            <div>
                <h3 className="text-sm font-semibold text-gray-400 mb-2 uppercase">Audio</h3>
                <input type="file" accept="audio/*" ref={musicInputRef} className="hidden" onChange={(e) => handleFileUpload(e, 'Music')} />
                <input type="file" accept="audio/*" ref={sfxInputRef} className="hidden" onChange={(e) => handleFileUpload(e, 'SFX')} />
                
                <div className="space-y-2">
                     <button onClick={() => musicInputRef.current?.click()} className="w-full flex items-center p-2 rounded-md bg-gray-700/50 hover:bg-gray-700 transition-colors">
                        <MusicIcon className="w-5 h-5 mr-3 text-green-400" />
                        <span className="text-sm">Upload Music</span>
                    </button>
                     <button onClick={() => sfxInputRef.current?.click()} className="w-full flex items-center p-2 rounded-md bg-gray-700/50 hover:bg-gray-700 transition-colors">
                        <SoundIcon className="w-5 h-5 mr-3 text-blue-400" />
                        <span className="text-sm">Upload Sound Effect</span>
                    </button>
                    {musicAssets.map(asset => (
                         <div key={asset.id} className="flex items-center p-2 rounded-md bg-gray-800/60">
                            <MusicIcon className="w-5 h-5 mr-3 text-gray-400 flex-shrink-0" />
                            <p className="text-sm truncate">{asset.name}</p>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};
